import dayjs from '../../utils/dayjs';
import React from 'react';

import { DataTable, EmptyState, Layout, Page, Text, Link, Badge, Card, Icon, InlineStack } from '@shopify/polaris';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import emptyIMG from '../../media/images/empty.png';
import Pagination from '../../components/pagination';
import helpers from '../../helpers';

import SkeletonLoading from 'components/skeletonPageLoading';
import SimpleFilter from 'components/simpleFilter';
import { ___ } from 'languages/index';
import { TypedJourney, useGetJourneys } from 'queries/journey.query';
import { Helmet } from 'react-helmet-async';
import SeatingModal from './journey.modal';
import { ChevronRightIcon, PlusIcon, StarFilledIcon } from '@shopify/polaris-icons';

function sortJourneysByParent(journeys: TypedJourney[]): TypedJourney[] {
  const journeyIds = new Set(journeys.map((journey) => String(journey.id)));
  const childrenByParent = new Map<string, TypedJourney[]>();
  const sortedJourneys: TypedJourney[] = [];
  const addedJourneyIds = new Set<string>();

  journeys.forEach((journey) => {
    const parentId = journey.journey_parent == null ? '' : String(journey.journey_parent);
    if (!parentId || parentId === '0') return;

    const children = childrenByParent.get(parentId) || [];
    children.push(journey);
    childrenByParent.set(parentId, children);
  });

  const appendJourneyWithChildren = (journey: TypedJourney) => {
    const journeyId = String(journey.id);
    if (addedJourneyIds.has(journeyId)) return;

    addedJourneyIds.add(journeyId);
    sortedJourneys.push(journey);
    childrenByParent.get(journeyId)?.forEach(appendJourneyWithChildren);
  };

  journeys
    .filter((journey) => {
      const parentId = journey.journey_parent == null ? '' : String(journey.journey_parent);
      return !parentId || parentId === '0' || !journeyIds.has(parentId);
    })
    .forEach(appendJourneyWithChildren);

  // Giữ lại dữ liệu lỗi quan hệ hoặc bị vòng lặp thay vì làm mất khỏi bảng.
  journeys.forEach(appendJourneyWithChildren);

  return sortedJourneys;
}

export default function JourneyList() {
  const [entities, setEntities] = useState<TypedJourney[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const history = useNavigate();

  /**
   * If user apply filter, it will add to URL, then parse URL back to initial state
   */
  let { search } = useLocation();
  let StringQuery: any = helpers.ExtractUrl(search);

  const initialQuery = {
    query: '',
    page: 1,
    limit: 20,
    sort: 'createdAt:desc',
  };
  const [mainQuery, setMainQuery] = useState({
    ...initialQuery,
    ...StringQuery,
  });

  const { data, refetch: getEntities, isPending: loading } = useGetJourneys(mainQuery);

  useEffect(() => {
    if (data) {
      setEntities(data.body);
      setTotalItems(data.totalItems);
    }
  }, [data]);

  /**
   * Change page number
   */
  const onChangePagination = useCallback(
    (numPage: number, limit: number) => {
      setMainQuery({ ...mainQuery, page: numPage, limit: limit });
    },
    [mainQuery]
  );

  /**
   * Double request!
   */
  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) history('/journey' + buildURLSearch);
    getEntities();
  }, [mainQuery]);

  /**
   * I do not know ...
   * Bug: In React, do NOT remove this code
   */
  const filterCallback = useCallback((_value: any) => {
    if (_value === false) {
      return setMainQuery(initialQuery);
    } else {
      setMainQuery((oldValue) => {
        return { ...oldValue, ..._value };
      });
    }
  }, []);

  const [newModelactive, setNewModelactive] = useState(false);
  const toggleNewActive = useCallback(() => setNewModelactive((active) => !active), []);

  const currentJourneyID = useRef(null);

  const emptyData = (
    <EmptyState heading="Không có bản ghi!" image={emptyIMG}>
      <p>Oh, Không có gì ở đây. Hãy thử bỏ bộ lọc hoặc tạo mới.</p>
    </EmptyState>
  );

  const renderItem = (vehicle: TypedJourney) => {
    const {
      id,
      journey_name,
      journey_description,
      journey_from,
      journey_from_province_id,
      journey_to,
      journey_to_province_id,
      journey_default_price,
      journey_total_time,
      journey_distance,
      journey_status,
      createdAt,
      is_default,
      journey_parent,
      journey_type,

      seating,
    } = vehicle;
    const isChildJourney = journey_parent != null && String(journey_parent) !== '0';

    return [
      <Link
        key={id}
        url={`./view/${id}`}
        onClick={() => {
          // currentJourneyID.current = id;
          // toggleNewActive();
        }}
      >
        <div style={{ paddingLeft: isChildJourney ? '20px' : 0, minWidth: '160px' }}>
          <InlineStack wrap={false} gap="100" blockAlign="center">
            {isChildJourney ? <Icon source={ChevronRightIcon} /> : null}
            {is_default ? <Icon source={StarFilledIcon} /> : null}
            <strong>{journey_name}</strong>
          </InlineStack>
        </div>
      </Link>,
      journey_type === 'outbound' ? <Badge tone="info">Chiều đi</Badge> : journey_type === 'inbound' ? <Badge tone="attention">Chiều về</Badge> : '-',
      journey_from,
      journey_to,
      helpers.formatNumberWithCommas(journey_default_price),
      helpers.minuteToFullHourString(journey_total_time),
      helpers.formatNumberWithCommas(journey_distance),
      seating?.seating_name || '-',
      journey_status === 1 ? <Badge tone="success">Hoạt động</Badge> : <Badge tone="warning">Ngừng hoạt động</Badge>,
      createdAt ? dayjs(new Date(Number(createdAt))).format('HH:mm:ss DD-MM-YYYY') : '-',
    ];
  };

  const PagesList =
    entities?.length > 0 ? (
      <>
        <DataTable
          hoverable
          fixedFirstColumns={1}
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
          headings={[
            'Tên hành trình',
            'Chiều',
            'Từ',
            'Đến',
            'Giá mặc định',
            'Tổng thời gian',
            'Khoảng cách',
            'Loại xe mặc định',
            'Tình trạng',
            'Tạo',
          ]}
          rows={sortJourneysByParent(entities).map(renderItem)}
          hideScrollIndicator
          footerContent={___('Show page {display_page_number} of {total_record_number} results', {
            display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
            total_record_number: <Text as="strong">{totalItems}</Text>,
          })}
        />
      </>
    ) : (
      emptyData
    );

  const Actual_page = (
    <>
      <Helmet>
        <title>Danh sách hành trình</title>
      </Helmet>
      <Page
        title="Hành trình"
        primaryAction={{
          content: 'Thêm hành trình',
          disabled: false,
          icon: PlusIcon,
          onAction: toggleNewActive,
        }}
      >
        <Layout>
          <Layout.Section>
            <Card padding="0">
              <SimpleFilter loading={loading} onCallback={filterCallback} options={[]} sortField={[]} />
              {PagesList}
            </Card>
            <br />
            {totalItems > mainQuery.limit ? (
              <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
            ) : null}
          </Layout.Section>
        </Layout>
      </Page>
      {
        <SeatingModal
          show={newModelactive}
          currentJourneyID={currentJourneyID.current}
          onClose={() => {
            currentJourneyID.current = null;
            setNewModelactive(false);
          }}
        />
      }
    </>
  );

  return <>{entities === null ? <SkeletonLoading /> : Actual_page}</>;
}
